import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/v2-components/ui/sheet";
import KeyIcon from "@/assets/icons/KeyIcon";
import CloseIcon from "@/v2-components/core/icon/close-icon";
import FilterIcon from "@/v2-components/core/icon/filter-icon";
import InfoIcon from "@/v2-components/core/icon/info-icon";
import RefreshIcon from "@/v2-components/core/icon/refresh-icon";
import { getFilterIcon } from "@/v2-components/pages/games/_utils";
import { SUPPORT_TYPE_DESC } from "@/v2-components/pages/games/_const";
import { cn } from "@/libs/utils";
import { useTrans } from "@/utils/useTrans";
import type { VrGamesFilterState } from "@/v2-components/pages/games/vr-games/use-vr-games-filters";

/**
 * `/games/vr/games` **모바일** 필터 (시안 `30035:18131`, 360×946).
 *
 * ## 데스크탑 레일과 왜 다른 컴포넌트인가
 *
 * 같은 필터인데 **표현이 다르다.** 레일은 상시 노출 + 체크박스 행 + 항목마다 개수이고,
 * 이건 바텀시트 + **칩 그리드**에 개수가 없다. 한 컴포넌트에 `variant` 를 두면 행/칩,
 * 개수 유무, 접힘 유무, 하단 바 유무가 전부 분기라 읽기가 더 어려워진다.
 *
 * 대신 **상태는 하나**를 공유한다(`useVrGamesFilters`) — 두 표현이 같은 `toggle` ·
 * `reset` · `groups` 를 쓰므로 필터 의미가 갈라지지 않는다. 갈라질 수 있는 건 표현뿐이다.
 *
 * ## 즉시 적용이다 (「확인」이 아니다)
 *
 * 칩을 누르면 **바로** 목록에 반영된다. 하단 CTA 는 「적용」이 아니라 **닫기**다 —
 * 시안이 `View 128 Games` 처럼 **지금 걸린 필터의 결과 수**를 그 버튼에 쓰고 있고,
 * 하단에 선택된 칩이 실시간으로 쌓이기 때문이다. 확정 방식이었다면 그 숫자가 확정 전
 * 값이라 의미가 없다.
 *
 * ## 시안 수치
 *
 * ```
 *   Header   64        제목 16/22 · 닫기 24 (좌우 16)
 *   섹션 간   24        라벨 14/18 → 목록 12
 *   칩       h36 r8    아이콘 20 + 간격 4 + 12/16 · pl8/pr12 (아이콘 없으면 px12)
 *            선택      bg `blue-10` · border `blue-30` · text `blue-60`
 *            비선택    bg white   · border `coolgray-20` · text `coolgray-90`
 *   Bottom   124       초기화 36×36 + 선택 칩(가로 스크롤) · CTA 328×40
 * ```
 *
 * ⚠️ **지원 방식만 2열 고정폭(160)** 이다 — 다른 그룹은 내용 폭이다. 시안 좌표
 * (`30035:18177`~`18180` 이 전부 160 · x 0/168)에서 그대로 왔다.
 */
export default function VrGamesFilterSheet({
  state,
  className,
}: {
  state: VrGamesFilterState;
  className?: string;
}) {
  const { t } = useTrans(["games", "gnb"]);
  const [open, setOpen] = useState(false);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        {/* 시안 `29942:17605` — 48×48 · **radius 4** · 보더 `#dde1e6` · 배경 없음 · 아이콘 24.
            ★radius 를 8 로 그렸었다 — 옆 검색 박스(4)와 어긋난다. */}
        <button
          type="button"
          aria-label={t("Filter")}
          className={cn(
            "relative flex h-12 w-12 shrink-0 items-center justify-center rounded border border-coolgray-20 text-coolgray-90",
            className,
          )}
        >
          <FilterIcon className="h-6 w-6" />
          {/**
           * 시안에 없는 점 하나를 더한다. 시트를 닫으면 **무엇이 걸려 있는지 보이지
           * 않는데**(모바일엔 레일이 없다) 목록만 줄어 있으면 「왜 적게 나오지」가 된다.
           * 데스크탑은 레일이 상시 노출이라 이 문제가 없다.
           */}
          {state.hasAnyFilter && (
            <span
              aria-hidden
              className="absolute right-2 top-2 h-2 w-2 rounded-full bg-blue-60"
            />
          )}
        </button>
      </SheetTrigger>

      <SheetContent
        side="bottom"
        /**
         * 시트가 스크롤 컨테이너가 아니라 **본문만** 스크롤한다 — 하단 바가 붙어 있어야 한다.
         *
         * ★**`laptop:p-0` 이 필요하다.** `SheetContent` 의 cva 기본이 `p-4 laptop:p-8` 인데
         * tailwind-merge 는 **같은 breakpoint 안에서만** 충돌을 해소한다 — `p-0` 은 `p-4` 만
         * 지우고 `laptop:p-8` 은 살아남는다(`docs/pitfalls.md` #8(tailwind-merge 는 같은 breakpoint 안에서만)).
         *
         * 이 시트는 트리거가 `laptop:hidden` 이라 데스크탑에서 **열 수는** 없지만, 열어 둔
         * 채로 창을 넓히면 그대로 적용된다. 실측: 360 에서 열고 1440 으로 넓히니 패딩이
         * **0 → 32** 가 됐다. 제목도 같은 이유로 아래에서 덮는다.
         */
        className="flex flex-col gap-0 p-0 laptop:p-0"
      >
        {/* 시안 `30035:18163` — 64 · 좌우 16. */}
        <div className="flex h-16 shrink-0 items-center justify-between px-4">
          {/* 기본값이 `text-base font-600 ... laptop:text-2xl laptop:font-400` 이라
              `laptop:` 도 덮어야 한다 — 위 `laptop:p-0` 과 같은 이유(실측 22px/400). */}
          <SheetTitle className="text-lg font-600 text-coolgray-100 laptop:text-lg laptop:font-600">
            {t("Filter")}
          </SheetTitle>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label={t("Close")}
            className="text-coolgray-90"
          >
            <CloseIcon className="h-6 w-6" />
          </button>
        </div>

        {/* 시안 `30035:18166` — 섹션 간 24. 길어지면 여기만 스크롤. */}
        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-4 pb-6 pt-2">
          <Section label={t("Game key")}>
            <Chip
              checked={state.gameKeyOnly}
              onClick={state.toggleGameKey}
              icon={KeyIcon}
              label={t("Game key available")}
            />
          </Section>

          {state.groups.map((group) => (
            <Section
              key={group.key}
              label={t(group.label)}
              // 시안 `30035:18173` — 지원 방식에만 「Learn more」가 붙는다.
              help={
                group.key === "supportType" ? (
                  <SupportTypeHelp
                    rows={group.rows.map((r) => ({
                      value: r.value,
                      label: r.label,
                    }))}
                  />
                ) : undefined
              }
              // 지원 방식만 2열 고정폭이다(위 주석 참조).
              grid={group.key === "supportType"}
            >
              {group.rows.map((row) => {
                const Icon = getFilterIcon(row.value);
                return (
                  <Chip
                    key={row.value}
                    checked={row.checked}
                    // 켜져 있으면 개수가 0 이어도 **끌 수 있어야** 한다 —
                    // 레일과 같은 규칙이다.
                    disabled={row.count === 0 && !row.checked}
                    onClick={() => state.toggle(group.key, row.value)}
                    icon={Icon ?? undefined}
                    label={t(row.label)}
                    className={group.key === "supportType" ? "w-full" : ""}
                  />
                );
              })}
            </Section>
          ))}
        </div>

        {/* 시안 `30035:18208` — 하단 124, 위 경계선. */}
        <div className="shrink-0 border-t border-coolgray-20 px-4 pb-5 pt-4">
          {state.hasAnyFilter && (
            // 시안 `30035:18209` — 초기화 36×36 + 선택 칩 목록(가로 스크롤).
            <div className="mb-4 flex items-center gap-4">
              <button
                type="button"
                onClick={state.reset}
                aria-label={t("Reset filters")}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-coolgray-10 text-coolgray-70"
              >
                <RefreshIcon className="h-5 w-5" />
              </button>
              {/* 칩이 넘치면 가로로 흐른다 — 시안도 328 안에 662 를 담고 있다. */}
              {/* `scrollbar-none` 사유는 형제 `pc-games-filter-sheet.tsx` 주석 (#5709). */}
              <div className="scrollbar-none flex min-w-0 flex-1 gap-2 overflow-x-auto">
                {state.groups.flatMap((group) =>
                  group.rows
                    .filter((row) => row.checked)
                    .map((row) => {
                      const Icon = getFilterIcon(row.value);
                      return (
                        <Chip
                          key={`${group.key}-${row.value}`}
                          checked
                          onClick={() => state.toggle(group.key, row.value)}
                          icon={Icon ?? undefined}
                          label={t(row.label)}
                          removable
                        />
                      );
                    }),
                )}
                {state.gameKeyOnly && (
                  <Chip
                    checked
                    onClick={state.toggleGameKey}
                    icon={KeyIcon}
                    label={t("Game key available")}
                    removable
                  />
                )}
              </div>
            </div>
          )}

          {/**
           * 시안 `30035:18218` — 328×40 풀폭 CTA. 문구가 **결과 수**를 담는다.
           * 위 주석대로 「적용」이 아니라 닫기다.
           */}
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="flex h-10 w-full items-center justify-center rounded-full bg-blue-60 text-base font-600 text-white"
          >
            {t("View {{count}} games", { count: state.games.length })}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

/** 시안 `30035:18167` 등 — 라벨 14/18 SemiBold → 목록까지 12. */
function Section({
  label,
  help,
  grid,
  children,
}: {
  label: string;
  help?: React.ReactNode;
  grid?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3">
      <h3 className="text-base font-600 text-coolgray-100">{label}</h3>
      {help}
      <div
        className={cn(
          "gap-2",
          grid ? "grid grid-cols-2" : "flex flex-wrap items-start",
        )}
      >
        {children}
      </div>
    </section>
  );
}

/**
 * 칩 — 값은 파일 상단 표 참조.
 *
 * `<button>` 하나로 그린다. 레일이 Radix `Checkbox` 를 쓰는 것과 달리 여기엔 중첩
 * 버튼 문제(`vr-games-filter-rail.tsx` 주석)가 없다 — 라벨을 감싸지 않기 때문이다.
 */
function Chip({
  checked,
  disabled,
  onClick,
  icon: Icon,
  label,
  removable,
  className,
}: {
  checked: boolean;
  disabled?: boolean;
  onClick: () => void;
  icon?: React.ComponentType<{ className?: string }>;
  label: string;
  removable?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={checked}
      className={cn(
        "flex h-9 shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded-lg border text-sm tracking-[0.4px] transition-colors",
        Icon ? "pl-2 pr-3" : "px-3",
        checked
          ? "border-blue-30 bg-blue-10 text-blue-60"
          : "border-coolgray-20 bg-white text-coolgray-90",
        disabled && "pointer-events-none opacity-40",
        className,
      )}
    >
      {Icon && <Icon className="h-5 w-5 shrink-0" />}
      {label}
      {/* 하단 바의 칩만 X 가 붙는다(시안 `30035:18212`). 누르면 그 필터가 꺼진다. */}
      {removable && <CloseIcon className="h-4 w-4 shrink-0" />}
    </button>
  );
}

/**
 * 시안 `30035:18173` — info 20 + 밑줄 「Learn more」.
 *
 * 레일은 같은 설명을 `HoverCard` 로 띄우는데 **모바일엔 hover 가 없다.** 그래서 여기서는
 * 눌러서 펼친다. 설명 문구는 `SUPPORT_TYPE_DESC` 하나만 쓴다 — 레일과 같은 출처라
 * 같은 개념 설명이 화면마다 갈라지지 않는다.
 */
function SupportTypeHelp({
  rows,
}: {
  rows: { value: string; label: string }[];
}) {
  const { t } = useTrans(["games", "gnb"]);
  const [shown, setShown] = useState(false);

  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        onClick={() => setShown((v) => !v)}
        aria-expanded={shown}
        className="flex items-center gap-2 self-start text-coolgray-70"
      >
        <InfoIcon className="h-5 w-5 text-coolgray-40" />
        <span className="text-sm underline">{t("Learn more")}</span>
      </button>

      {shown && (
        <dl className="flex flex-col gap-2 rounded-lg bg-coolgray-10 p-3">
          {rows.map((row) => (
            <div key={row.value} className="flex flex-col gap-0.5">
              <dt className="text-sm font-600 text-coolgray-90">
                {t(row.label)}
              </dt>
              <dd className="text-sm text-coolgray-70">
                {t(SUPPORT_TYPE_DESC[row.value])}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}
